/**
 * ThemeContext — manages custom colour scheme (background, accent, text).
 *
 * YADA is dark-only by design. No light theme, no system preference.
 * Users can freely customise the three primary colour tokens; all
 * secondary tokens (panel, border, muted, hover states, etc.) are
 * derived automatically and applied as inline CSS custom properties
 * on <html> so the change takes effect instantly without re-renders.
 *
 * Persists the user's palette in localStorage under `yada:custom-colors`.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export interface CustomColors {
  bg: string;
  accent: string;
  text: string;
}

export const DEFAULT_COLORS: CustomColors = {
  bg: '#0b0c0f',
  accent: '#10b981',
  text: '#eaeaea',
};

export function isDefaultColors(colors: Partial<CustomColors> | null | undefined): boolean {
  if (!colors) return true;
  return (
    (!colors.bg || colors.bg.toLowerCase() === DEFAULT_COLORS.bg.toLowerCase()) &&
    (!colors.accent || colors.accent.toLowerCase() === DEFAULT_COLORS.accent.toLowerCase()) &&
    (!colors.text || colors.text.toLowerCase() === DEFAULT_COLORS.text.toLowerCase())
  );
}

const STORAGE_KEY = 'yada:custom-colors';
const CONTRAST_KEY = 'yada:high-contrast';

interface ThemeContextValue {
  customColors: CustomColors;
  setCustomColors: (colors: CustomColors) => void;
  resetColors: () => void;
  highContrast: boolean;
  setHighContrast: (value: boolean) => void;
  prefersMoreContrast: boolean;
}

const ThemeContext = createContext<ThemeContextValue>({
  customColors: DEFAULT_COLORS,
  setCustomColors: () => undefined,
  resetColors: () => undefined,
  highContrast: false,
  setHighContrast: () => undefined,
  prefersMoreContrast: false,
});

// ── Colour math utilities ─────────────────────────────────────────────────────

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn: h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6; break;
      case gn: h = ((bn - rn) / d + 2) / 6; break;
      case bn: h = ((rn - gn) / d + 4) / 6; break;
    }
  }
  return [h * 360, s * 100, l * 100];
}

function hslToHex(h: number, s: number, l: number): string {
  const hn = h / 360, sn = s / 100, ln = l / 100;
  let r: number, g: number, b: number;
  if (sn === 0) {
    r = g = b = ln;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      let tt = t;
      if (tt < 0) tt += 1;
      if (tt > 1) tt -= 1;
      if (tt < 1 / 6) return p + (q - p) * 6 * tt;
      if (tt < 1 / 2) return q;
      if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
      return p;
    };
    const q = ln < 0.5 ? ln * (1 + sn) : ln + sn - ln * sn;
    const p = 2 * ln - q;
    r = hue2rgb(p, q, hn + 1 / 3);
    g = hue2rgb(p, q, hn);
    b = hue2rgb(p, q, hn - 1 / 3);
  }
  return '#' + [r, g, b]
    .map(x => Math.round(Math.max(0, Math.min(1, x)) * 255).toString(16).padStart(2, '0'))
    .join('');
}

function adjustLightness(hex: string, delta: number): string {
  const [r, g, b] = hexToRgb(hex);
  const [h, s, l] = rgbToHsl(r, g, b);
  return hslToHex(h, s, Math.max(0, Math.min(100, l + delta)));
}

function mixHex(hex1: string, hex2: string, ratio: number): string {
  const [r1, g1, b1] = hexToRgb(hex1);
  const [r2, g2, b2] = hexToRgb(hex2);
  return '#' + [
    Math.round(r1 * ratio + r2 * (1 - ratio)),
    Math.round(g1 * ratio + g2 * (1 - ratio)),
    Math.round(b1 * ratio + b2 * (1 - ratio)),
  ].map(x => Math.max(0, Math.min(255, x)).toString(16).padStart(2, '0')).join('');
}

// ── Apply colours to DOM ──────────────────────────────────────────────────────

function applyColors(colors: CustomColors, highContrast: boolean) {
  const { bg, accent, text } = colors;
  const el = document.documentElement;

  // Core tokens (user-chosen)
  el.style.setProperty('--bg', bg);
  el.style.setProperty('--accent', accent);
  el.style.setProperty('--text', text);

  // Derived from bg
  const panel = adjustLightness(bg, 6);
  el.style.setProperty('--panel',            panel);
  el.style.setProperty('--dropdown-bg',      panel);
  el.style.setProperty('--border',           adjustLightness(bg, 14));
  el.style.setProperty('--input-bg',         bg);
  el.style.setProperty('--panel-hover',      adjustLightness(bg, 9));
  el.style.setProperty('--bg-hover',         adjustLightness(bg, 4));
  el.style.setProperty('--border-color-hover', adjustLightness(bg, 22));
  el.style.setProperty('--layer-2',          adjustLightness(bg, 10));

  // Derived from text — 75% baseline, 90% when high contrast is active
  const mutedRatio = highContrast ? 0.90 : 0.75;
  el.style.setProperty('--muted', mixHex(text, bg, mutedRatio));

  // Derived from accent
  el.style.setProperty('--accent-hover',  adjustLightness(accent, -8));
  el.style.setProperty('--accent-active', adjustLightness(accent, -16));

  // Always dark
  el.setAttribute('data-theme', 'dark');
}

// ── Contrast persistence ──────────────────────────────────────────────────────

function loadHighContrast(): boolean {
  try { return localStorage.getItem(CONTRAST_KEY) === 'true'; } catch { return false; }
}

function saveHighContrast(value: boolean) {
  try { localStorage.setItem(CONTRAST_KEY, String(value)); } catch {}
}

// ── Persistence ───────────────────────────────────────────────────────────────

function loadColors(): CustomColors {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<CustomColors>;
      if (parsed.bg && parsed.accent && parsed.text) {
        return { bg: parsed.bg, accent: parsed.accent, text: parsed.text };
      }
    }
  } catch {
    // ignore
  }
  return DEFAULT_COLORS;
}

function saveColors(colors: CustomColors) {
  try {
    if (isDefaultColors(colors)) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(colors));
    }
  } catch {
    // ignore
  }
}

// ── Provider ──────────────────────────────────────────────────────────────────

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [customColors, setCustomColorsState] = useState<CustomColors>(loadColors);
  const [highContrast, setHighContrastState] = useState<boolean>(loadHighContrast);
  const [prefersMoreContrast, setPrefersMoreContrast] = useState<boolean>(
    () => window.matchMedia('(prefers-contrast: more)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-contrast: more)');
    const handler = (e: MediaQueryListEvent) => setPrefersMoreContrast(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Listen for hydration / cross-tab theme changes
  useEffect(() => {
    const handleThemeChange = () => {
      setCustomColorsState(loadColors());
      setHighContrastState(loadHighContrast());
    };
    window.addEventListener('yada:theme-change', handleThemeChange);
    return () => window.removeEventListener('yada:theme-change', handleThemeChange);
  }, []);

  const effectiveHighContrast = highContrast || prefersMoreContrast;

  useEffect(() => {
    applyColors(customColors, effectiveHighContrast);
  }, [customColors, effectiveHighContrast]);

  const setCustomColors = useCallback((colors: CustomColors) => {
    saveColors(colors);
    setCustomColorsState(colors);
    window.dispatchEvent(new CustomEvent('yada:theme-change'));
    window.dispatchEvent(new CustomEvent('yada:accountSetting:changed'));
  }, []);

  const resetColors = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
    setCustomColorsState(DEFAULT_COLORS);
    window.dispatchEvent(new CustomEvent('yada:theme-change'));
    window.dispatchEvent(new CustomEvent('yada:accountSetting:changed'));
  }, []);

  const setHighContrast = useCallback((value: boolean) => {
    saveHighContrast(value);
    setHighContrastState(value);
    window.dispatchEvent(new CustomEvent('yada:theme-change'));
    window.dispatchEvent(new CustomEvent('yada:accountSetting:changed'));
  }, []);

  return (
    <ThemeContext.Provider value={{
      customColors, setCustomColors, resetColors,
      highContrast, setHighContrast, prefersMoreContrast,
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
